<template>
  <div class="fixed inset-0 flex items-center justify-center bg-gray-900/60 backdrop-blur-sm z-50 p-4">
    <div class="bg-white rounded-md shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto relative animate-fade-in">
      
      <!-- Close Button -->
      <button @click="$emit('close')" class="absolute top-4 right-4 rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 transition" aria-label="Close">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div class="p-6 sm:p-8">
        <div class="text-center mb-6">
          <h2 class="font-serif text-2xl font-bold text-gray-900">Publish Advertisement</h2>
          <p class="text-gray-500 text-sm mt-1">Share your content with the community.</p>
        </div>

        <form @submit.prevent="submitAd" class="space-y-4">
          
          <!-- Title -->
          <div>
            <label class="form-label">Title</label>
            <input v-model="form.title" type="text" placeholder="e.g. Upcoming Art Exhibition" required class="form-input" />
          </div>

          <!-- Description -->
          <div>
            <label class="form-label">Description</label>
            <textarea v-model="form.description" rows="3" placeholder="Describe your advertisement..." required class="form-input resize-none"></textarea>
          </div>

          <!-- Image Upload -->
          <div>
            <label class="form-label">Image</label>
            <div 
              class="border-2 border-dashed border-gray-300 rounded-md p-5 text-center cursor-pointer hover:border-brand-red/50 hover:bg-red-50/30 transition"
              @click="$refs.fileInput.click()"
            >
              <input type="file" ref="fileInput" @change="handleImage" class="hidden" accept="image/*" />
              
              <div v-if="!form.image" class="text-gray-500">
                <p class="text-sm font-medium">Click to upload image</p>
                <p class="text-xs mt-1">PNG, JPG up to 5MB</p>
              </div>
              <div v-else class="flex items-center justify-center gap-2">
                 <span class="text-green-600 text-sm font-medium truncate">{{ form.image.name }}</span>
                 <button type="button" @click.stop="form.image = null" class="text-red-500 hover:text-red-700">✕</button>
              </div>
            </div>
          </div>

          <!-- Publish Date (Optional) -->
          <div>
              <label class="form-label">Publish Date</label>
              <input v-model="form.publish_date" type="date" class="form-input" />
          </div>

          <!-- Submit Button -->
          <button 
            type="submit" 
            :disabled="loading"
            class="btn-primary w-full py-3 !mt-6"
          >
            {{ loading ? 'Submitting...' : 'Submit for Approval' }}
          </button>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { authHeaders } from '@/api';
import Swal from 'sweetalert2';

export default {
  name: 'AdvertisementForm',
  data() {
    return {
      loading: false,
      form: {
        title: '',
        description: '',
        image: null,
        publish_date: new Date().toISOString().substr(0, 10)
      }
    };
  },
  methods: {
    async handleImage(e) {
      if (e.target.files.length > 0) {
        try {
          const file = e.target.files[0];
          this.form.image = await this.compressImage(file);
        } catch (error) {
          console.error("Image compression failed:", error);
          this.form.image = e.target.files[0]; // Fallback to original
        }
      }
    },
    compressImage(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = (event) => {
          const img = new Image();
          img.src = event.target.result;
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const MAX_WIDTH = 1200;
            const MAX_HEIGHT = 1200;
            let width = img.width;
            let height = img.height;

            if (width > height) {
              if (width > MAX_WIDTH) {
                height *= MAX_WIDTH / width;
                width = MAX_WIDTH;
              }
            } else {
              if (height > MAX_HEIGHT) {
                width *= MAX_HEIGHT / height;
                height = MAX_HEIGHT;
              }
            }

            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0, width, height);

            canvas.toBlob((blob) => {
              if (!blob) {
                reject(new Error('Canvas is empty'));
                return;
              }
              const compressedFile = new File([blob], file.name, {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              resolve(compressedFile);
            }, 'image/jpeg', 0.8);
          };
          img.onerror = (error) => reject(error);
        };
        reader.onerror = (error) => reject(error);
      });
    },
    async submitAd() {
      const userString = localStorage.getItem('user');
      if (!userString) {
        Swal.fire({
          icon: 'error',
          title: 'Authentication Required',
          text: 'Please log in to publish an advertisement.',
          confirmButtonColor: '#BF3131'
        });
        return;
      }

      const user = JSON.parse(userString);
      
      if (!this.form.image) {
          Swal.fire({
            icon: 'warning',
            title: 'Image Required',
            text: 'Please upload an image for your advertisement.',
            confirmButtonColor: '#BF3131'
          });
          return;
      }

      this.loading = true;

      try {
        const payload = new FormData();
        payload.append('user_id', user.id);
        payload.append('title', this.form.title);
        payload.append('description', this.form.description);
        payload.append('image', this.form.image);
        payload.append('publish_date', this.form.publish_date);

        // Use env variable directly (it already includes /api)
        const baseUrl = process.env.VUE_APP_API_BASE_URL || 'https://sanskritirdapoon.in/api';

        await axios.post(`${baseUrl}/advertisments`, payload, {
          headers: { 'Content-Type': 'multipart/form-data', ...authHeaders() }
        });

        Swal.fire({
          icon: 'success',
          title: 'Submitted!',
          text: 'Advertisement submitted successfully! It will be reviewed for approval.',
          confirmButtonColor: '#BF3131'
        });
        this.$emit('close');
        
      } catch (err) {
        console.error("Ad submission error:", err);
        Swal.fire({
          icon: 'error',
          title: 'Submission Failed',
          text: 'Failed to submit advertisement. Please try again.',
          confirmButtonColor: '#BF3131'
        });
      } finally {
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.animate-fade-in {
  animation: fadeIn 0.3s ease-out;
}
@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
</style>
